import { useEffect, useState } from 'react';
import { X, Download, Share } from 'lucide-react';
import { onPwaEvent, triggerInstall } from '../../utils/pwa';

const DISMISS_KEY = 'zuriya:pwa-install-dismissed-at';
const DISMISS_WINDOW_DAYS = 14;
const IOS_DISMISS_KEY = 'zuriya:pwa-ios-tip-dismissed-at';

function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPadOS reports MacIntel + touch, handle that too.
  const iPad =
    ua.includes('Mac') && typeof document !== 'undefined' && 'ontouchend' in document;
  return /iPhone|iPod/.test(ua) || iPad;
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const when = parseInt(raw, 10);
    if (!Number.isFinite(when)) return false;
    const days = (Date.now() - when) / (1000 * 60 * 60 * 24);
    return days < DISMISS_WINDOW_DAYS;
  } catch {
    return false;
  }
}

function markDismissed(key) {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    // localStorage may be unavailable (private mode on older Safari).
  }
}

/**
 * InstallPrompt
 *
 * Bottom-sheet banner that:
 *   - Android / desktop Chromium: shows the native install prompt via the
 *     deferred `beforeinstallprompt` event.
 *   - iOS Safari: shows a one-time "Add to Home Screen" tooltip since
 *     Safari doesn't fire the prompt event.
 *
 * Respects a 14-day dismissal window via localStorage so we don't nag.
 */
export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;

    // iOS path: no prompt event, show a small tooltip a few seconds after
    // first visit to avoid flashing at page load.
    if (isIos()) {
      if (recentlyDismissed(IOS_DISMISS_KEY)) return undefined;
      const t = setTimeout(() => {
        setIosMode(true);
        setVisible(true);
      }, 4000);
      return () => clearTimeout(t);
    }

    // Android / Chromium path.
    if (recentlyDismissed(DISMISS_KEY)) return undefined;
    const off = onPwaEvent('installAvailable', () => setVisible(true));
    const offInstalled = onPwaEvent('installed', () => {
      setVisible(false);
      markDismissed(DISMISS_KEY);
    });
    return () => {
      off();
      offInstalled();
    };
  }, []);

  if (!visible) return null;

  const handleInstall = async () => {
    const outcome = await triggerInstall();
    if (outcome === 'accepted' || outcome === 'dismissed') {
      markDismissed(DISMISS_KEY);
    }
    setVisible(false);
  };

  const handleDismiss = () => {
    markDismissed(iosMode ? IOS_DISMISS_KEY : DISMISS_KEY);
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[60] animate-fade-in-up"
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Install Zuriya"
    >
      <div
        className="mx-auto max-w-md rounded-2xl border border-[#E6E4DD] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.18)] p-4 flex items-start gap-3"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="h-11 w-11 rounded-xl bg-[#1A1A1A] text-[#D4AF37] flex items-center justify-center font-heading text-xl font-bold flex-shrink-0">
          Z
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-[#1A1A1A] text-sm">Install Zuriya</p>
          {iosMode ? (
            <p className="text-xs text-stone-500 mt-1 leading-relaxed">
              Tap <Share className="inline h-3 w-3 mx-0.5 -mt-0.5" /> Share, then
              {' '}<span className="font-medium text-[#1A1A1A]">Add to Home Screen</span> for
              one-tap access to bookings and messages.
            </p>
          ) : (
            <p className="text-xs text-stone-500 mt-1 leading-relaxed">
              Add Zuriya to your home screen for faster access and instant
              booking updates.
            </p>
          )}
          {!iosMode && (
            <button
              type="button"
              onClick={handleInstall}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#D4AF37] px-3 py-2 text-xs font-semibold text-[#1A1A1A] hover:bg-[#C5A028] transition-colors"
              data-testid="pwa-install-confirm-btn"
            >
              <Download className="h-3.5 w-3.5" /> Install
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-stone-400 hover:text-stone-700 -mr-1 -mt-1 p-1"
          data-testid="pwa-install-dismiss-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
